import { createContext, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { CustomModal, CustomerServiceDrawer, toast } from "../components";
import BindEmailPopup from "../components/BindEmailPopup";
import BindMobilePopup from "../components/BindMobilePopup";
import ConfirmValidatePopup from "../components/ConfirmValidatePopup";
import CustomerServiceListDrawer from "../components/CustomerServiceListDrawer";
import { disableLoginContextPathList } from "../enumerations/disablePathList";
import localStorageKey from "../enumerations/localStorageKey";
import { useGetUserInfo, useLogout } from "../hooks/personal/personal.hook";
import { useDefaultLayout } from "../layouts/defaultLayout";
import { envChangeType, envType } from "../plugins/utils";

// 試玩按鈕文字，i18n 試算表還沒有 normal.tryPlay 時用這組
const TRY_PLAY_TEXT = {
  cn: "试玩",
  en: "Demo",
  vn: "Chơi thử",
  pt: "Jogar demo",
  jp: "デモプレイ",
  es: "Jugar demo",
  in: "डेमो खेलें",
  id: "Coba main",
  kr: "체험하기",
  th: "ทดลองเล่น",
  my: "Demo",
  bn: "ডেমো খেলুন",
  pk: "ڈیمو کھیلیں",
};

// jindingusd：有試玩時，未登入提示拿掉「取消」、未驗證提示拿掉「退出登入」，
// 只留「試玩」與「前往」；未驗證的會員也能試玩
const IS_DEMO_ONLY_PROMPT_SITE = process.env.REACT_APP_PRODUCTION_NAME_DEV === "jindingusd";

const LoginContext = createContext({
  isLogin: false,
  setIsLogin: () => {},
  checkIfLogin: () => {},
  canDemo: false,
  userInfo: {},
  setIsHaveInvitation: () => {},
  isHaveInvitation: false,
  setShowCustomerDrawer: () => {},
  csLink: undefined,
  activeCSLink: undefined,
  setActiveCSLink: () => {},
  openCSDrawer: () => {},
  isHaveUID: false,
  setIsHaveUID: () => {},
});

export const LoginContextProvider = ({ children }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [showNeedLoginModal, setShowNeedLoginModal] = useState(false);
  const [needLoginDemo, setNeedLoginDemo] = useState(null);
  const [showConfirmValidateModal, setShowConfirmValidateModal] = useState(false);
  const [showConfirmValidatePopup, setShowConfirmValidatePopup] = useState(false);
  const [showBindMobile, setShowBindMobile] = useState(false);
  const [showBindEmail, setShowBindEmail] = useState(false);
  const [isLogin, setIsLogin] = useState();
  const [isHaveInvitation, setIsHaveInvitation] = useState(false);
  const [isHaveUID, setIsHaveUID] = useState(false);

  const { mutate: logout } = useLogout();
  const { setCheckLoginProcess } = useDefaultLayout();
  const { data: userInfo } = useGetUserInfo({
    enabled: !!isLogin && !disableLoginContextPathList.includes(window.location.pathname),
  });

  const [activeCSLink, setActiveCSLink] = useState(undefined);
  const [showCustomerListDrawer, setShowCustomerListDrawer] = useState(false);
  const [showCustomerDrawer, setShowCustomerDrawer] = useState(false);

  const isNeedValidate = !!isLogin && userInfo?.mobile_status === 0 && userInfo?.email_status === 0;
  // 可以走試玩的狀態：未登入，或 (jindingusd) 已登入但未驗證
  const canDemo = !isLogin || (IS_DEMO_ONLY_PROMPT_SITE && isNeedValidate);

  // options.onDemo：未登入時，登入提示多一顆「試玩」按鈕，按下執行 onDemo
  // jindingusd 未驗證的驗證提示也會有「試玩」
  const checkIfLogin = (options) => {
    const onDemo = typeof options?.onDemo === "function" ? options.onDemo : null;
    if (!isLogin) {
      setNeedLoginDemo(() => onDemo);
      setShowNeedLoginModal(true);
      return false;
    } else {
      if (isNeedValidate) {
        setNeedLoginDemo(() => (IS_DEMO_ONLY_PROMPT_SITE ? onDemo : null));
        setShowConfirmValidateModal(true);
        return false;
      }
      return true;
    }
  };

  const demoBtnText = needLoginDemo
    ? t("normal.tryPlay", { defaultValue: TRY_PLAY_TEXT[i18n.language] ?? "Demo" })
    : "";
  const isDemoOnlyPrompt = IS_DEMO_ONLY_PROMPT_SITE && !!needLoginDemo;

  const openCSDrawer = () => {
    if (isLogin) {
      // setActiveCSLink(csLink);
      setShowCustomerDrawer(true);
    } else {
      // setShowCustomerListDrawer(true);
      setShowCustomerDrawer(true);
    }
  };

  const getTimeZoneAndCurrency = () => {
    const currencySetting = window.localStorage.getItem(localStorageKey.DEFAULT_CURRENCY);
    if (currencySetting == "rmb") {
      window.localStorage.setItem(
        localStorageKey.DEFAULT_CURRENCY,
        envChangeType("REACT_APP_DEFAULT_CURRENCY"),
      );
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } else if (isLogin == false && !currencySetting) {
      //isLogin有可能是undefined
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const currency =
        envChangeType("REACT_APP_TIMEZONE_CURRENCY", envType.OBJECT)[timeZone] ??
        envChangeType("REACT_APP_DEFAULT_CURRENCY");
      console.debug("currency", currency);
      window.localStorage.setItem(localStorageKey.DEFAULT_CURRENCY, currency.toUpperCase());
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }
  };

  const handleCSDrawerVisible = () => {
    setShowCustomerDrawer(false);
    setActiveCSLink(undefined);
  };

  // useEffect(() => {
  //   if (csLink) {
  //     setActiveCSLink(csLink);
  //   }
  // }, [csLink]);

  useEffect(() => {
    const userIsLogin = Boolean(
      window.localStorage.getItem(localStorageKey.USER_INFO) &&
      window.localStorage.getItem(localStorageKey.TOKEN),
    );
    setIsLogin(userIsLogin);
  }, []);

  useEffect(() => {
    setCheckLoginProcess(checkIfLogin);
    if (userInfo) {
      const prevStorage = JSON.parse(localStorage.getItem(localStorageKey.USER_INFO));
      localStorage.setItem(
        localStorageKey.USER_INFO,
        JSON.stringify({
          ...prevStorage,
          userInfo: { user: { ...prevStorage.userInfo.user, ...userInfo } },
        }),
      );
    }
  }, [userInfo, isLogin]);

  useEffect(() => {
    getTimeZoneAndCurrency();
  }, [isLogin]);

  return (
    <LoginContext.Provider
      value={{
        isLogin,
        setIsLogin,
        checkIfLogin,
        canDemo,
        userInfo,
        setIsHaveInvitation,
        isHaveInvitation,
        isHaveUID,
        setIsHaveUID,
        setShowCustomerDrawer,
        // csLink,
        activeCSLink,
        setActiveCSLink,
        openCSDrawer,
      }}
    >
      {children} {/* NEED LOGIN MODAL */}
      <CustomModal
        isModalVisible={showNeedLoginModal}
        setIsModalVisible={setShowNeedLoginModal}
        title={t("normal.notLoggedIn")}
        content={t("normal.notLoggedIn.hint")}
        needIcon={true}
        okBtnText={t("normal.forwardTo")}
        cancelBtnText={t("normal.cancel")}
        isHaveCancel={!isDemoOnlyPrompt}
        extraBtnText={demoBtnText}
        onExtra={() => {
          setShowNeedLoginModal(false);
          needLoginDemo?.();
        }}
        onOk={() => {
          setShowNeedLoginModal(false);
          if (isHaveInvitation || isHaveUID) {
            navigate(`/signup`);
          } else {
            navigate("/signin");
          }
        }}
      />
      {/* VALIDATION VERIFICATION */}
      <CustomModal
        isModalVisible={showConfirmValidateModal}
        setIsModalVisible={setShowConfirmValidateModal}
        title={t("verifymodal.title")}
        content={t("verifymodal.content")}
        needIcon={true}
        okBtnText={t("verifymodal.goValidate")}
        cancelBtnText={t("personal.signOut")}
        showCloseButton={true}
        isHaveCancel={!isDemoOnlyPrompt}
        extraBtnText={demoBtnText}
        onExtra={() => {
          setShowConfirmValidateModal(false);
          needLoginDemo?.();
        }}
        onOk={() => {
          setShowConfirmValidateModal(false);
          setShowConfirmValidatePopup(true);
        }}
        onCancel={logout}
      />
      <ConfirmValidatePopup
        visible={showConfirmValidatePopup}
        setVisible={setShowConfirmValidatePopup}
        onMobileClick={() => {
          setShowBindMobile(true);
        }}
        onEmailClick={() => {
          setShowBindEmail(true);
        }}
      />
      {/* BIND EMAIL AND PHONE POPUPS */}
      <BindEmailPopup
        visible={showBindEmail}
        setVisible={setShowBindEmail}
        onBeforeFinish={() => {
          setShowBindEmail(false);
          setShowConfirmValidatePopup(false);
          setTimeout(() => {
            toast({ content: t("normal.success"), type: "success" });
            window.location.reload();
          }, 1000);
        }}
      />
      <BindMobilePopup
        visible={showBindMobile}
        setVisible={setShowBindMobile}
        onBeforeFinish={() => {
          setShowBindMobile(false);
          setShowConfirmValidatePopup(false);
          setTimeout(() => {
            toast({ content: t("normal.success"), type: "success" });
            window.location.reload();
          }, 1000);
        }}
      />
      <CustomerServiceDrawer
        activeCSLink={activeCSLink}
        visible={showCustomerDrawer}
        handleVisible={handleCSDrawerVisible}
      />
      <CustomerServiceListDrawer
        visible={showCustomerListDrawer}
        handleVisible={setShowCustomerListDrawer}
      />
    </LoginContext.Provider>
  );
};
export const useLogin = () => {
  const LoginValue = useContext(LoginContext);
  return LoginValue;
};
